import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Download, Smartphone, AlertTriangle, CheckCircle2, Loader2, ExternalLink } from 'lucide-react';

/**
 * Task #362 (2026-08-20): SEB-test landingsside.
 *
 * Kandidat får e-post 48h+24h før eksamen med lenke hit. Formålet er å teste
 * at Safe Exam Browser fungerer på maskinen FØR eksamensdagen — så eventuelle
 * problemer kan løses i tide.
 *
 * Flyt:
 *   1. Vis kandidat-info + tydelig veiledning
 *   2. Vipps CIBA-verifisering (må matche registrert mobilnummer)
 *   3. Last ned .seb-config-fil
 *   4. SEB åpnes, henter test-spørsmål, kandidat svarer
 *   5. Fullført-bekreftelse
 */

type Step = 'info' | 'phone' | 'vipps_waiting' | 'config_ready' | 'seb_launched' | 'in_seb' | 'done' | 'error';

interface CandidateInfo {
  candidate: { full_name: string; mobile_masked: string | null };
  exam: {
    instance_name: string;
    exam_window_start: string;
    exam_set_title: string;
    host_organization: string;
  };
  completed: string | null;
}

interface Question {
  id: string;
  text: string;
  type: string;
  options: string[] | null;
}

export default function SebTestPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const initialConfigToken = searchParams.get('config_token');

  const [step, setStep] = useState<Step>(initialConfigToken ? 'in_seb' : 'info');
  const [info, setInfo] = useState<CandidateInfo | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [configToken, setConfigToken] = useState<string | null>(initialConfigToken);
  const [question, setQuestion] = useState<Question | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isInSeb = navigator.userAgent.includes('SEB/');

  // Hent kandidat-info ved lasting
  useEffect(() => {
    if (!token) return;
    fetch(`/api/public/seb-test/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setInfo(d);
          if (d.completed && !initialConfigToken) {
            // Allerede fullført
            setStep('done');
          }
        } else {
          setError(d.message || d.error);
          setStep('error');
        }
      })
      .catch(() => {
        setError('Kunne ikke hente info om denne testlenken.');
        setStep('error');
      });
  }, [token, initialConfigToken]);

  // Når vi er i SEB, hent spørsmål automatisk
  useEffect(() => {
    if (step !== 'in_seb' || !isInSeb || !configToken || !token) return;
    fetch(`/api/public/seb-test/${token}/question?config_token=${configToken}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setQuestion(d.question);
        else {
          setError(d.message || d.error);
          setStep('error');
        }
      })
      .catch(() => {
        setError('Kunne ikke hente testspørsmål.');
        setStep('error');
      });
  }, [step, isInSeb, configToken, token]);

  // Cleanup polling
  useEffect(() => () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
  }, []);

  const startVipps = async () => {
    if (!phoneNumber || phoneNumber.replace(/\D/g, '').length < 8) {
      setError('Skriv inn et gyldig telefonnummer (8 sifre)');
      return;
    }
    setSubmitting(true);
    setStep('vipps_waiting');
    setError(null);

    try {
      const res = await fetch('/api/candidate/auth/vipps/ciba-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Kunne ikke starte Vipps');
      pollCiba(data.auth_req_id, data.poll_interval || 5);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke starte Vipps');
      setStep('phone');
      setSubmitting(false);
    }
  };

  const pollCiba = (authReqId: string, interval: number) => {
    const poll = async () => {
      try {
        const res = await fetch(`/api/candidate/auth/vipps/ciba-status/${authReqId}`);
        const data = await res.json();
        if (data.status === 'pending') return;
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        if (data.status === 'vipps_verified') {
          await verifyAndGetConfig(data.session_token);
          return;
        }
        if (data.status === 'rejected' || data.status === 'expired' || data.status === 'error') {
          setError(data.error || 'Vipps-autentisering mislyktes eller ble avvist');
          setStep('phone');
          setSubmitting(false);
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    };
    pollingRef.current = setInterval(poll, interval * 1000);
    setTimeout(poll, 1000);
  };

  const verifyAndGetConfig = async (sessionToken: string) => {
    try {
      const res = await fetch(`/api/public/seb-test/${token}/verify-vipps`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || data.error || 'Kunne ikke verifisere Vipps-innlogging');
        setStep('phone');
        setSubmitting(false);
        return;
      }
      setConfigToken(data.config_token);
      setStep('config_ready');
      setSubmitting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
      setStep('phone');
      setSubmitting(false);
    }
  };

  const downloadSebConfig = () => {
    if (!configToken || !token) return;
    // Nettleseren laster ned .seb-fil, SEB åpnes automatisk hvis installert
    window.location.href = `/api/public/seb-test/${token}/seb-config?config_token=${configToken}`;
    setStep('seb_launched');
  };

  const submitAnswer = async () => {
    if (!configToken || !token) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/public/seb-test/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config_token: configToken })
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Kunne ikke registrere fullføring');
        setSubmitting(false);
        return;
      }
      setStep('done');
      setSubmitting(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ukjent feil');
      setSubmitting(false);
    }
  };

  // === RENDER ===

  if (step === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2 text-center">Kunne ikke laste SEB-test</h1>
          <p className="text-gray-700 text-center">{error}</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const examDate = new Date(info.exam.exam_window_start).toLocaleString('nb-NO', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-t-lg shadow-md p-6 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-10 h-10 text-blue-600 flex-shrink-0" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Test at Safe Exam Browser fungerer</h1>
              <p className="text-gray-600 mt-1">Sjekk at alt er klart <strong>før</strong> eksamensdagen</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md p-6 space-y-6">
          {/* Info-boks: eksamen-detaljer */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-900 space-y-1">
              <div><strong>Kandidat:</strong> {info.candidate.full_name}</div>
              <div><strong>Eksamen:</strong> {info.exam.instance_name}</div>
              <div><strong>Prøvesett:</strong> {info.exam.exam_set_title}</div>
              <div><strong>Dato:</strong> {examDate}</div>
              <div><strong>Arrangør:</strong> {info.exam.host_organization}</div>
            </div>
          </div>

          {/* STEG: info (start) */}
          {step === 'info' && (
            <div className="space-y-4">
              <div className="prose prose-sm max-w-none text-gray-700">
                <h3 className="text-lg font-semibold text-gray-900">Hvorfor må jeg gjøre dette nå?</h3>
                <p>
                  Selve eksamen kjøres i Safe Exam Browser (SEB) — en spesiell nettleser
                  som låser maskinen din under prøven. Vi anbefaler at du sjekker at det
                  fungerer på maskinen din <strong>flere dager før eksamen</strong>, slik at
                  eventuelle problemer kan løses i tide.
                </p>
                <h3 className="text-lg font-semibold text-gray-900 mt-4">Hva skjer nå?</h3>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Du logger inn med <strong>Vipps</strong> — for å bekrefte at det er du som tester (vi sammenligner med telefonnummeret arrangøren registrerte deg med: {info.candidate.mobile_masked}).</li>
                  <li>Du laster ned en SEB-konfigurasjonsfil som starter SEB på maskinen din.</li>
                  <li>Inne i SEB får du ett enkelt eksempelspørsmål å svare på.</li>
                  <li>Når du er ferdig, får du bekreftelse på at oppsettet fungerer.</li>
                </ol>
                <p className="mt-3">Hele testen tar under 2 minutter.</p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-900">
                  <strong>Har du ikke SEB installert?</strong> Du kan laste ned Safe Exam
                  Browser fra{' '}
                  <a
                    href="https://safeexambrowser.org/download_en.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline inline-flex items-center gap-1"
                  >
                    safeexambrowser.org <ExternalLink className="w-3 h-3" />
                  </a>
                  {' '}(velg versjon for Windows, Mac eller iPad).
                </p>
              </div>

              <button
                type="button"
                onClick={() => setStep('phone')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                Kom i gang med testen
              </button>
            </div>
          )}

          {/* STEG: phone (Vipps-input) */}
          {step === 'phone' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Logg inn med Vipps</h3>
              <p className="text-sm text-gray-700">
                Skriv inn ditt telefonnummer — det må være det samme som arrangøren
                registrerte deg med ({info.candidate.mobile_masked}).
              </p>
              <div className="relative">
                <Smartphone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="98765432"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={submitting}
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <button
                type="button"
                onClick={startVipps}
                disabled={submitting}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                {submitting ? 'Starter...' : 'Logg inn med Vipps'}
              </button>
            </div>
          )}

          {/* STEG: vipps_waiting */}
          {step === 'vipps_waiting' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Åpne Vipps på telefonen din</h3>
              <p className="text-sm text-gray-600 mt-2">
                Godkjenn innloggingen i Vipps-appen. Denne siden oppdaterer seg automatisk.
              </p>
            </div>
          )}

          {/* STEG: config_ready */}
          {step === 'config_ready' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-green-900">Vipps-verifisering fullført</p>
                  <p className="text-sm text-green-800">Klar til å starte SEB-testen.</p>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Last ned SEB-konfigurasjon</h3>
              <p className="text-sm text-gray-700">
                Klikk knappen under for å laste ned en <code className="bg-gray-100 px-1 rounded">.seb</code>-fil.
                Nettleseren din vil spørre om åpning i Safe Exam Browser. Godkjenn dette.
                SEB starter automatisk og viser testen.
              </p>
              <button
                type="button"
                onClick={downloadSebConfig}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" />
                Last ned SEB-konfigurasjon og start test
              </button>
              <p className="text-xs text-gray-500 text-center">
                Har du problemer? Sjekk at SEB er installert. Om ikke, last ned fra{' '}
                <a href="https://safeexambrowser.org/download_en.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                  safeexambrowser.org
                </a>.
              </p>
            </div>
          )}

          {/* STEG: seb_launched */}
          {step === 'seb_launched' && (
            <div className="text-center py-8 space-y-3">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900">Åpner Safe Exam Browser…</h3>
              <p className="text-sm text-gray-600">
                Hvis SEB ikke åpnet seg automatisk, sjekk nedlastinger-mappen og
                dobbeltklikk på filen <code className="bg-gray-100 px-1 rounded">seb-test.seb</code>.
              </p>
            </div>
          )}

          {/* STEG: in_seb (kandidat er inne i SEB) */}
          {step === 'in_seb' && !isInSeb && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="font-semibold text-red-900">Dette må åpnes i Safe Exam Browser</p>
              <p className="text-sm text-red-800 mt-1">
                Denne testsiden må åpnes i SEB. Gå tilbake til e-postlenken og start på nytt.
              </p>
            </div>
          )}

          {step === 'in_seb' && isInSeb && question && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-900 font-semibold">
                  ✓ SEB er aktiv. Svar på spørsmålet under for å fullføre testen.
                </p>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Testspørsmål</h3>
              <p className="text-gray-800 whitespace-pre-line">{question.text}</p>
              {question.options && question.options.length > 0 ? (
                <div className="space-y-2">
                  {question.options.map((opt, i) => (
                    <label key={i} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="answer"
                        value={String(i)}
                        checked={selectedAnswer === String(i)}
                        onChange={() => setSelectedAnswer(String(i))}
                        className="mt-1"
                      />
                      <span className="text-sm text-gray-800">{opt}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <textarea
                  value={selectedAnswer}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  rows={4}
                  placeholder="Skriv svaret ditt her..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              )}
              <p className="text-xs text-gray-500">
                Merk: Svaret ditt vurderes ikke — dette er kun en test av at SEB fungerer.
              </p>
              <button
                type="button"
                onClick={submitAnswer}
                disabled={submitting || !selectedAnswer}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold py-3 px-4 rounded-lg transition"
              >
                {submitting ? 'Fullfører...' : 'Fullfør SEB-test'}
              </button>
            </div>
          )}

          {step === 'in_seb' && isInSeb && !question && (
            <div className="text-center py-8">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
              <p className="text-sm text-gray-600 mt-3">Henter testspørsmål...</p>
            </div>
          )}

          {/* STEG: done */}
          {step === 'done' && (
            <div className="text-center py-8">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900">SEB-testen er fullført!</h3>
              <p className="text-gray-700 mt-3">
                Ditt Safe Exam Browser-oppsett fungerer riktig. Du er klar for eksamensdagen.
              </p>
              <p className="text-sm text-gray-500 mt-4">
                Møter du opp på eksamensdagen med samme maskin og SEB installert, går alt greit.
                Du kan lukke denne siden nå.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
